import { lazy } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import RootLayout from "./RootLayout";
import { queryClient as appQueryClient } from "./api/queryClient";
import { loadPageData, prefetchPageData } from "./pageData";
import { hasHydrated } from "./appHydration";
import SessionService from "./services/SessionService";
import ByRole from "./components/Access/ByRole";
import RequireAudience from "./components/Access/RequireAudience";
import RequireAuth from "./components/Access/RequireAuth";
import RequireModule from "./components/Access/RequireModule";
import RequireRole from "./components/Access/RequireRole";
import RequireUnregistered from "./components/Access/RequireUnregistered";
import { Audience, audienceForRoles } from "./components/Access/audience";
import { ensureIdentity, readAudience, readRoles, readUserId } from "./api/identityCache";
import {
  CALENDAR_MANAGER_ROLES,
  INFUSION_SCHEDULE_ROLES,
  MRI_REVIEW_ROLES,
  PATIENT_RECORD_STAFF_ROLES,
  Role,
} from "./components/Access/Role";
// Not lazy: the access guards already pull it into the main chunk, so a dynamic import here
// would only add a build warning.
import PageNotFound from "./components/PageNotFound";
import RequireSubscription from "./components/Subscription/RequireSubscription";
import RouteError from "./components/Common/RouteError";
import { moduleRouteObjects } from "@/modules/contributions";
import { ModuleAudience } from "@/modules/types";

/**
 * The application's single static route tree.
 *
 * Until now the app picked one of three per-audience trees at runtime, after /identity resolved.
 * That made route-level loaders impossible (a data router wants a tree it can match before
 * rendering), so the trees are merged here and the audience decides at the element level instead.
 *
 * Two consequences worth keeping in mind when editing this file:
 *
 *  - A path that exists for one audience now exists for ALL of them. Access control used to come
 *    for free from a path simply being absent, and most clinical routes carry only RequireAuth —
 *    so every route below must be wrapped in RequireAudience, or shared via ByRole. Leaving a
 *    route unwrapped hands it to patients and external providers.
 *  - Guards stay INSIDE the audience wrapper, in the same order they nested before, so an audience
 *    that should not see a path gets PageNotFound rather than a guard's redirect.
 *
 * `src/routeResolution.smoke.test.tsx` pins this behavior for every audience.
 */

// --- Clinical staff pages ---
const Audit = lazy(() => import("./components/Audit/Audit"));
// Lazy, so the chart library only ever loads for a developer who opens this page.
const DevInsightsPage = lazy(() => import("./components/DevInsights/DevInsightsPage"));
const AppointmentPage = lazy(() => import("./pages/AppointmentPage"));
const Calendar = lazy(() => import("./components/Calendar/Calendar"));
const Designer = lazy(() => import("./components/Designer/Designer"));
const SiteSettings = lazy(() => import("./components/SiteSettings/SiteSettings"));
const ExternalProviders = lazy(() => import("./components/ExternalProviders/ExternalProviders"));
const Locations = lazy(() => import("./components/Locations/Locations"));
const Patients = lazy(() => import("./components/Patients/Patients"));
const Treatment = lazy(() => import("./components/Treatment/Treatment"));
const ImagingCenters = lazy(() => import("./components/ImagingCenters/ImagingCenters"));
const UserPage = lazy(() => import("./components/Users/UserPage/UserPage"));
const UsersPage = lazy(() => import("./components/Users/UsersPage"));
const MtcDashboard = lazy(() => import("./components/Dashboard/Internal/MtcDashboard"));
const MrisNeedingApprovalList = lazy(() => import("./components/Dashboard/Internal/MrisNeedingApprovalList"));
const AllOrdersList = lazy(() => import("./components/Dashboard/Internal/AllOrdersList"));
const InfusionsNeedingOrdersList = lazy(() => import("./components/Dashboard/Internal/InfusionsNeedingOrders"));
const PreInfusionChecks = lazy(() => import("./components/Dashboard/Internal/PreInfusionChecks"));
const Messages = lazy(() => import("./components/Messages/Messages"));
const ReferralsPage = lazy(() => import("./components/Referrals/ReferralsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const ProviderCalendar = lazy(() => import("./pages/ProviderCalendar"));
const UserCalendar = lazy(() => import("./components/Calendar/UserCalendar"));
const ProviderMedicationRefillsPage = lazy(() => import("./components/MedicationRefills/MedicationRefillsPage"));
const InterestedUsersPage = lazy(() => import("./pages/InterestedUsersPage"));
const LaunchWaitlistPage = lazy(() => import("./pages/LaunchWaitlistPage"));
const AppointmentManagementPage = lazy(() => import("./pages/AppointmentManagementPage"));
const QuestionnaireListPage = lazy(() => import("./components/QuestionnairePlatform/QuestionnaireListPage"));
const QuestionnaireEditorPage = lazy(() => import("./components/QuestionnairePlatform/QuestionnaireEditorPage"));

// --- Patient pages ---
const PatientMessagesPage = lazy(() => import("./pages/PatientMessagesPage"));
const PatientDashboard = lazy(() => import("./components/Dashboard/Patient/PatientDashboard"));
const HelpPage = lazy(() =>
  import("./pages/HelpPage").then((m) => ({ default: m.HelpPage })),
);
const DocumentUpdatesPage = lazy(() => import("./pages/DocumentUpdatesPage"));
const GoodFaithEstimatePage = lazy(() => import("./pages/GoodFaithEstimatePage"));
const ReconsentPage = lazy(() => import("./pages/ReconsentPage"));
const GoodFaithEstimates = lazy(() => import("./components/Dashboard/Internal/GoodFaithEstimates"));
const MedicalInformationPage = lazy(() =>
  import("./pages/MedicalInformationPage").then((m) => ({
    default: m.MedicalInformationPage,
  })),
);
const ScheduleAppointment = lazy(() => import("./pages/ScheduleAppointment"));
const PatientAppointments = lazy(() => import("./pages/PatientAppointments"));
const TreatmentSummaryPage = lazy(() =>
  import("./pages/TreatmentSummaryPage").then((m) => ({
    default: m.TreatmentSummaryPage,
  })),
);
const MedicationRefillsPage = lazy(() => import("./pages/MedicationRefillsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const SubscriptionCancelPage = lazy(() => import("./pages/SubscriptionCancelPage"));
const PatientRegistrationForm = lazy(() => import("./components/Registration/Patient/Register"));
const QuestionnaireRunnerPage = lazy(() => import("./components/QuestionnairePlatform/QuestionnaireRunnerPage"));

// --- External provider pages ---
const ExternalDashboard = lazy(() => import("./components/Dashboard/External/ExternalDashboard"));
const CaregiverDashboard = lazy(() => import("./components/Dashboard/Caregiver/CaregiverDashboard"));
const CaregiverOversightPage = lazy(() => import("./components/Caregivers/CaregiverOversightPage"));
const ProviderSettings = lazy(() => import("./components/ExternalProvider/ProviderSettings"));
const ExternalPatients = lazy(() => import("./components/ExternalProviders/ExternalPatients"));
const ExternalProviderRegister = lazy(() => import("./components/Registration/ExternalProvider/Register"));

// --- Shared pages ---
const LandingPage = lazy(() => import("./components/LandingPage"));
const Notifications = lazy(() => import("./components/Notifications/Notifications"));
const AccountPage = lazy(() => import("./components/Profile/ProfilePage"));

/**
 * Build a loader that resolves everything a page needs before the navigation commits: its lazy
 * chunk (the dynamic import) and then the queries it declares via `pageQueries`. The shell's
 * progress bar covers both, and the previous page stays on screen throughout instead of being
 * torn down for a skeleton.
 *
 * A route with no loader behaves exactly as it does today, so these go on one route at a time.
 *
 * A page's queries may be scoped to the signed-in user: the loader resolves identity through the
 * cache (`ensureIdentity`) and hands the id to `pageQueries`. It cannot call `useCerberus()` — a
 * loader runs outside React — which is why identity lives in the query cache at all.
 *
 * Three things keep it from fetching when it shouldn't. React Router runs a loader on match,
 * before the element decides what to render, so none of them are access checks — RequireAudience
 * on the element still is — they exist to stop the loader making a request the user's own
 * navigation never would:
 *
 *  - **Signed out**: skip. Otherwise an anonymous visitor typing /audit waits on a request that
 *    can only 401, instead of being shown the login page.
 *  - **Wrong audience**: skip. A patient typing /audit used to get a clean 404 with no request;
 *    without this they would get the audit log's fetch and an error dialog.
 *  - **Before first paint**: warm without waiting. On a hard load there is no previous page to
 *    hold on screen, so blocking would just leave it blank until the fetch returned. The page
 *    still gets a primed cache; it simply renders its own loading state meanwhile.
 *
 * A 401 raised in here still hits the shared client's interceptor, which clears the session and
 * hard-redirects. That is a full reload mid-navigation rather than a router `redirect()`; it is
 * correct, just not seamless, and is worth revisiting when more routes carry loaders.
 */
type ImportPage = () => Promise<unknown>;

/** Which page this route renders for a given audience, or nothing if it renders none. */
type PickPage = (audience: Audience | null) => ImportPage | undefined;

const loaderFor = (client: QueryClient, pick: PickPage) => async () => {
  if (!SessionService.getInstance().isAuthenticated()) return null;

  if (!hasHydrated()) {
    // Fire and forget — the navigation must not wait on the very first paint. The audience is
    // whatever the cache already holds, which on a cold load is usually nothing.
    const importPage = pick(readAudience(client));
    if (importPage) {
      const ctx = { userId: readUserId(client), roles: readRoles(client) };
      importPage().then((mod) => prefetchPageData(client, mod, ctx));
    }
    return null;
  }

  // Identity first, and awaited rather than read: it decides whether this loader may fetch at all,
  // and which page it is fetching for. Every loader in the navigation asks for the same cache key,
  // so they share one request — and on a client-side navigation it is already in cache, so this
  // costs nothing before the chunk starts.
  const identity = await ensureIdentity(client);
  if (!identity) return null;

  const importPage = pick(audienceForRoles(identity.roles));
  if (!importPage) return null;

  await loadPageData(client, await importPage(), { userId: identity.id, roles: identity.roles });
  return null;
};

/** One page, optionally restricted to the single audience the route is meant for. */
const pageLoader = (
  client: QueryClient,
  importPage: ImportPage,
  audience?: Audience,
) =>
  loaderFor(client, (current) =>
    audience && current && current !== audience ? undefined : importPage,
  );

/**
 * The loader half of `<ByRole>`: a path that renders a different page per audience. An audience
 * with no entry gets nothing fetched, exactly as it gets PageNotFound rendered.
 */
const byRoleLoader = (
  client: QueryClient,
  pages: Partial<Record<Audience, ImportPage>>,
) => loaderFor(client, (current) => (current ? pages[current] : undefined));

/**
 * Give every route its own error boundary.
 *
 * React Router renders an `errorElement` in place of the route that failed, so a page that throws
 * — while rendering, or from its loader — is contained to the shell's content region and the rest
 * of the app keeps working. Without one the error bubbles to the root route and React Router
 * replaces the whole shell with its default error page.
 *
 * Applied here rather than spelled out on ~50 routes so a new route cannot forget it.
 */
function withErrorElement(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) =>
    route.errorElement ? route : { ...route, errorElement: <RouteError /> },
  );
}

/** Module contributions, restricted to the audience that declared them. */
function moduleRoutes(audience: ModuleAudience, as: Audience): RouteObject[] {
  return moduleRouteObjects(audience).map((route) => ({
    ...route,
    element: <RequireAudience audience={as}>{route.element}</RequireAudience>,
  }));
}

/**
 * Build the tree bound to a specific QueryClient, so loaders prime the same cache the components
 * read. Production uses the app's singleton; tests pass their own — without this the loader would
 * fill one client while the rendered page read another, and every loader test would pass while
 * proving nothing.
 */
export function createAppRoutes(client: QueryClient): RouteObject[] {
  return withErrorElement([
  // --- Same page for everyone ---
  // Signed-in users have no business on the login page; send them home.
  { path: "/login", element: <Navigate to="/dashboard" replace /> },
  // Invitation and self-signup links live in the unauthenticated tree, so once a session exists
  // there is nothing left to accept. They must still RESOLVE here, for the moment the accept flow
  // signs the user in while the URL is still the invitation: the shell swaps to this tree on that
  // render, and without these the invite path fell through to "*" and flashed the 404 page on the
  // way to the dashboard. Audience-agnostic on purpose — every portal is invited this way.
  { path: "/i/new-patient", element: <Navigate to="/" replace /> },
  // Stripe's return address after enrollment payment (/enroll?paid=1). While enrollment is still
  // unfinished RootLayout shows the enrollment screen at ANY path, so this route is only reached
  // once the membership is Active — by then the next stop is registration, which the dashboard's
  // guard sends an unregistered patient to. Without it the return from Stripe was a 404.
  { path: "/enroll", element: <Navigate to="/dashboard" replace /> },
  { path: "/i/:invitationId", element: <Navigate to="/" replace /> },
  { path: "/i/p/:invitationId", element: <Navigate to="/" replace /> },
  // "Wasn't you? Secure my account" from the new-device sign-in email. Usually opened signed out
  // (UnauthenticatedRouting has the same path), but the owner may click it in a browser that is
  // still signed in — and it must work there too. Audience-agnostic: every account gets the email.
  {
    path: "/secure-account/:token",
    lazy: () => import("./components/Auth/SecureAccount").then((m) => ({ Component: m.default })),
  },
  {
    index: true,
    element: (
      <RequireAuth>
        <LandingPage />
      </RequireAuth>
    ),
  },
  {
    path: "/notifications",
    element: (
      <RequireAuth>
        <Notifications />
      </RequireAuth>
    ),
  },

  // --- Shared paths that resolve differently per audience ---
  {
    path: "/dashboard",
    // The clinical dashboard is deliberately absent: it fetches nothing itself, and each of its
    // cards is separately module- and role-gated, so a loader would have to guess which of them
    // will render and could fetch for a card that is switched off.
    loader: byRoleLoader(client, {
      patient: () => import("./components/Dashboard/Patient/PatientDashboard"),
      external: () => import("./components/Dashboard/External/ExternalDashboard"),
    }),
    element: (
      <ByRole
        clinical={
          <RequireAuth>
            <MtcDashboard />
          </RequireAuth>
        }
        patient={
          <RequireAuth>
            <PatientDashboard />
          </RequireAuth>
        }
        external={
          <RequireAuth>
            <ExternalDashboard />
          </RequireAuth>
        }
        caregiver={
          <RequireAuth>
            <CaregiverDashboard />
          </RequireAuth>
        }
      />
    ),
  },
  {
    path: "/messages",
    loader: byRoleLoader(client, {
      clinical: () => import("./components/Messages/Messages"),
      external: () => import("./components/Messages/Messages"),
      patient: () => import("./pages/PatientMessagesPage"),
    }),
    element: (
      <ByRole
        clinical={
          <RequireAuth>
            <Messages />
          </RequireAuth>
        }
        patient={
          <RequireAuth>
            <PatientMessagesPage />
          </RequireAuth>
        }
        external={
          <RequireAuth>
            <Messages />
          </RequireAuth>
        }
      />
    ),
  },
  {
    // Split out rather than folded into an optional `:threadId?` param: patients have no threaded
    // inbox, so /messages/<anything> must stay a 404 for them.
    path: "/messages/:threadId",
    loader: byRoleLoader(client, {
      clinical: () => import("./components/Messages/Messages"),
      external: () => import("./components/Messages/Messages"),
    }),
    element: (
      <ByRole
        clinical={
          <RequireAuth>
            <Messages />
          </RequireAuth>
        }
        external={
          <RequireAuth>
            <Messages />
          </RequireAuth>
        }
      />
    ),
  },
  {
    path: "/account/*",
    loader: byRoleLoader(client, {
      clinical: () => import("./components/Profile/ProfilePage"),
      patient: () => import("./components/Profile/ProfilePage"),
      caregiver: () => import("./components/Profile/ProfilePage"),
      external: () => import("./components/ExternalProvider/ProviderSettings"),
    }),
    element: (
      <ByRole
        clinical={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
        patient={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
        external={
          <RequireAuth>
            <ProviderSettings />
          </RequireAuth>
        }
        caregiver={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
      />
    ),
  },
  {
    // Both audiences nest their own <Routes> beneath this splat to pick up :patientId.
    path: "/patients/*",
    element: (
      <ByRole
        clinical={
          // PatientsController's class gate (AdminOrMedicalAssistantOrNurseOrProviderOrMtcDoc):
          // Developer, Auth Team and PAC Team would land on a page whose every call 403s.
          <RequireRole roles={PATIENT_RECORD_STAFF_ROLES}>
            <RequireAuth>
              <Patients />
            </RequireAuth>
          </RequireRole>
        }
        external={
          <RequireAuth>
            <ExternalPatients />
          </RequireAuth>
        }
      />
    ),
  },
  {
    path: "/appointment/:appointmentId",
    element: (
      <ByRole
        clinical={
          <RequireAuth>
            {/* Both clinician roles. MtcDoc is TeleHeadache's doctor and owns the appointment —
                Provider-only meant a TH clinician could be assigned a visit they could not join.
                The API re-checks that the appointment is theirs; this only decides who sees the
                page. */}
            <RequireRole roles={[Role.Provider, Role.MtcDoc]}>
              <AppointmentPage />
            </RequireRole>
          </RequireAuth>
        }
        patient={
          <RequireModule id="patient-visits">
            <RequireRole roles={[Role.Patient]}>
              <RequireAuth>
                <AppointmentPage />
              </RequireAuth>
            </RequireRole>
          </RequireModule>
        }
      />
    ),
  },
  {
    path: "/register",
    element: (
      <RequireUnregistered>
        <ByRole
          patient={<PatientRegistrationForm />}
          external={<ExternalProviderRegister />}
          // Clinical staff and caregivers are created by invitation and have no form to fill in
          // here. RequireAuth no longer sends them, so this only catches someone typing the URL —
          // send them home rather than to a 404. Loop-free precisely because of that change: the
          // dashboard cannot bounce an unregistered clinical user back to /register. Whoever adds
          // a caregiver registration flow should replace that branch with the form.
          clinical={<Navigate to="/dashboard" replace />}
          caregiver={<Navigate to="/dashboard" replace />}
        />
      </RequireUnregistered>
    ),
  },

  // --- Clinical staff only ---
  {
    path: "/caregiver-access",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="caregivers">
          <RequireRole roles={[Role.Admin]}>
            <RequireAuth>
              <CaregiverOversightPage />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/questionnaires",
    element: (
      // The questionnaire builder is local-development only (DEVELOPMENT_ONLY_MODULES).
      <RequireModule id="questionnaire-builder">
        <RequireAudience audience="clinical">
          <RequireRole roles={[Role.Admin, Role.Developer, Role.MtcDoc]}>
            <RequireAuth>
              <QuestionnaireListPage />
            </RequireAuth>
          </RequireRole>
        </RequireAudience>
      </RequireModule>
    ),
  },
  {
    path: "/questionnaires/versions/:versionId",
    element: (
      // The questionnaire builder is local-development only (DEVELOPMENT_ONLY_MODULES).
      <RequireModule id="questionnaire-builder">
        <RequireAudience audience="clinical">
          <RequireRole roles={[Role.Admin, Role.Developer, Role.MtcDoc]}>
            <RequireAuth>
              <QuestionnaireEditorPage />
            </RequireAuth>
          </RequireRole>
        </RequireAudience>
      </RequireModule>
    ),
  },
  {
    path: "/site-settings",
    loader: pageLoader(
      client,
      () => import("./components/SiteSettings/SiteSettings"),
      "clinical",
    ),
    element: (
      <RequireAudience audience="clinical">
        <RequireRole roles={[Role.Developer]}>
          <RequireAuth>
            <SiteSettings />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/calendar",
    loader: pageLoader(
      client,
      () => import("./components/Calendar/UserCalendar"),
      "clinical",
    ),
    element: (
      <RequireAudience audience="clinical">
        {/* ProviderTimeSlotsController: RoleNames.CalendarManagers. */}
        <RequireRole roles={CALENDAR_MANAGER_ROLES}>
          <RequireAuth>
            <UserCalendar />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/infusion-calendar",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="infusions">
          {/* GET /infusions: AdminOrMedicalAssistantOrNurseOrProviderOrMtcDoc. */}
          <RequireRole roles={INFUSION_SCHEDULE_ROLES}>
            <RequireAuth>
              <Calendar />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/audit",
    loader: pageLoader(client, () => import("./components/Audit/Audit"), "clinical"),
    element: (
      <RequireAudience audience="clinical">
        <RequireRole roles={[Role.Developer]}>
          <RequireAuth>
            <Audit />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    // Developer dashboard (2026-10-04): Developer only, like /audit — and the API refuses
    // everyone else independently ([Authorize(Roles = Developer)] on DevInsightsController).
    path: "/dev/insights",
    loader: pageLoader(client, () => import("./components/DevInsights/DevInsightsPage"), "clinical"),
    element: (
      <RequireAudience audience="clinical">
        <RequireRole roles={[Role.Developer]}>
          <RequireAuth>
            <DevInsightsPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    // Same roles as GET /launch-waitlist.
    path: "/launch-waitlist",
    element: (
      <RequireAudience audience="clinical">
        <RequireRole roles={[Role.Admin, Role.Developer]}>
          <RequireAuth>
            <LaunchWaitlistPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/interested-users",
    element: (
      <RequireAudience audience="clinical">
        {/* InterestedUsersController is [RequireModule(out-of-area-interest)]. */}
        <RequireModule id="out-of-area-interest">
          <RequireRole roles={[Role.Developer]}>
            <RequireAuth>
              <InterestedUsersPage />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/designer/*",
    element: (
      <RequireAudience audience="clinical">
        <RequireRole roles={[Role.Admin]}>
          <RequireAuth>
            <Designer />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/user",
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          <PageNotFound />
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/user/:userId/*",
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          <UserPage />
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/users/*",
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          <UsersPage />
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/external/*",
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          {/* TeleHeadache is direct-to-patient and has no referring providers, so the whole
              surface is off there — module-gated rather than role-gated, because the question
              is "does this deployment have referrers at all", not "who may manage them". */}
          <RequireModule id="external-providers">
            <RequireRole roles={[Role.Admin]}>
              <ExternalProviders />
            </RequireRole>
          </RequireModule>
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/locations/*",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="clinic-locations">
          <RequireAuth>
            <Locations />
          </RequireAuth>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/imaging-centers/*",
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          <ImagingCenters />
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/provider-calendar",
    element: (
      <RequireAudience audience="clinical">
        {/* ProviderTimeSlotsController: RoleNames.CalendarManagers. */}
        <RequireRole roles={CALENDAR_MANAGER_ROLES}>
          <RequireAuth>
            <ProviderCalendar />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/appointment-management",
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          <AppointmentManagementPage />
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/referrals",
    loader: pageLoader(
      client,
      () => import("./components/Referrals/ReferralsPage"),
      "clinical",
    ),
    element: (
      <RequireAudience audience="clinical">
        <RequireAuth>
          <ReferralsPage />
        </RequireAuth>
      </RequireAudience>
    ),
  },
  {
    path: "/provider/medication-refills",
    element: (
      <RequireAudience audience="clinical">
        {/* MTC Doc decides, Administrators view (RoleNames.MedicationRequestViewers); anyone else
            would only meet the server's 403. */}
        <RequireRole roles={[Role.MtcDoc, Role.Admin]}>
          <RequireAuth>
            <ProviderMedicationRefillsPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/treatment",
    element: (
      <RequireAudience audience="clinical">
        <RequireRole roles={[Role.Provider]}>
          <RequireAuth>
            <Treatment />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/mris",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="infusions">
          {/* MrisController: AdminOrMedicalAssistantOrProviderOrMtcDoc — the same roles the dashboard
              card is shown to. Provider-only here sent the other three to "not found" from it. */}
          <RequireRole roles={MRI_REVIEW_ROLES}>
            <RequireAuth>
              <MrisNeedingApprovalList />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    // CONS-5: the Practice's Good Faith Estimate queue (GoodFaithEstimatesController — Administrator).
    path: "/good-faith-estimates",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="billing">
          <RequireRole roles={[Role.Admin]}>
            <RequireAuth>
              <GoodFaithEstimates />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/billing",
    element: (
      <RequireAudience audience="clinical">
        {/* The billing endpoints are [RequireModule(billing)]: off for CerebraLinked. */}
        <RequireModule id="billing">
          <RequireRole roles={[Role.Admin]}>
            <RequireAuth>
              <BillingPage />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  // These two arrived on main (INF-6, INF-3/INF-10) while this branch had already replaced the
  // per-audience route files with the merged tree, so the merge surfaced them as a modify/delete
  // on a file that no longer exists. Re-added here with their original role gates, plus the
  // RequireAudience wrapper the merged tree requires — without it they would be reachable by
  // patients and external providers, since RequireRole alone does not bound the audience.
  {
    path: "/orders",
    element: (
      <RequireAudience audience="clinical">
        <RequireRole
          roles={[
            Role.Admin,
            Role.MedicalAssistant,
            Role.Nurse,
            Role.Provider,
            Role.MtcDoc,
          ]}
        >
          <RequireAuth>
            <AllOrdersList />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    // INF-7: PAC, Nurse and Administrator confirm the two pre-infusion items (InfusionsController.
    // PreInfusionCheckers). The 3-day reminder links here.
    path: "/pre-infusion-checks",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="infusions">
          <RequireRole roles={[Role.PacTeam, Role.Nurse, Role.Admin]}>
            <RequireAuth>
              <PreInfusionChecks />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/infusions-needing-order",
    element: (
      <RequireAudience audience="clinical">
        <RequireModule id="infusions">
          <RequireRole
            roles={[
              Role.MedicalAssistant,
              Role.PacTeam,
              Role.AuthTeam,
              Role.Provider,
              Role.MtcDoc,
            ]}
          >
            <RequireAuth>
              <InfusionsNeedingOrdersList />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  ...moduleRoutes("clinical", "clinical"),

  // --- Patients only ---
  {
    path: "/q/:key",
    element: (
      // Runs a Gen-3 questionnaire through the builder's runtime API, so it shares its gate.
      <RequireModule id="questionnaire-builder">
        <RequireAudience audience="patient">
          <RequireAuth>
            <QuestionnaireRunnerPage />
          </RequireAuth>
        </RequireAudience>
      </RequireModule>
    ),
  },
  {
    // CONS-5: ask for a Good Faith Estimate (MTC:Consent:GfeRequestUrl points here). RootLayout also
    // lets this path through during enrollment, when the patient notice first offers it.
    path: "/good-faith-estimate",
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="billing">
          <RequireRole roles={[Role.Patient]}>
            <RequireAuth>
              <GoodFaithEstimatePage />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    // CONS-3 follow-up: give consent to treatment again after withdrawing it.
    path: "/documents/reconsent",
    element: (
      <RequireAudience audience="patient">
        <RequireRole roles={[Role.Patient]}>
          <RequireAuth>
            <ReconsentPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    // CONS-4: sign materially revised documents (linked from the dashboard reminder and the email).
    path: "/documents/updates",
    element: (
      <RequireAudience audience="patient">
        <RequireRole roles={[Role.Patient]}>
          <RequireAuth>
            <DocumentUpdatesPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/help",
    element: (
      <RequireAudience audience="patient">
        <RequireRole roles={[Role.Patient]}>
          <RequireAuth>
            <HelpPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    path: "/medical-information",
    loader: pageLoader(
      client,
      () => import("./pages/MedicalInformationPage"),
      "patient",
    ),
    element: (
      <RequireAudience audience="patient">
        <RequireRole roles={[Role.Patient]}>
          <RequireAuth>
            <MedicalInformationPage />
          </RequireAuth>
        </RequireRole>
      </RequireAudience>
    ),
  },
  {
    // TeleHeadache-only. In CL mode (memory-care) the module is off and the page is a 404, so the
    // nav entry is hidden AND the URL is unreachable.
    path: "/schedule-appointment",
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="patient-visits">
          <RequireRole roles={[Role.Patient]}>
            <RequireAuth>
              <RequireSubscription>
                <ScheduleAppointment />
              </RequireSubscription>
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/patient-appointments",
    loader: pageLoader(
      client,
      () => import("./pages/PatientAppointments"),
      "patient",
    ),
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="patient-visits">
          <RequireRole roles={[Role.Patient]}>
            <RequireAuth>
              <PatientAppointments />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/treatment-summary",
    loader: pageLoader(
      client,
      () => import("./pages/TreatmentSummaryPage"),
      "patient",
    ),
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="patient-treatments">
          <RequireRole roles={[Role.Patient]}>
            <RequireAuth>
              <TreatmentSummaryPage />
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/medication-refills",
    loader: pageLoader(
      client,
      () => import("./pages/MedicationRefillsPage"),
      "patient",
    ),
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="medication-refills">
          <RequireRole roles={[Role.Patient]}>
            <RequireAuth>
              {/* The dashboard dialog checked this; the left-nav route here never did, so a
                  patient could reach the request form without a subscription. The server now
                  refuses the POST either way (RequireActiveSubscriptionAttribute) — this stops
                  the app offering a form it knows will be refused. */}
              <RequireSubscription>
                <MedicationRefillsPage />
              </RequireSubscription>
            </RequireAuth>
          </RequireRole>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    // Subscriptions belong to the billing module (a TeleHeadache default, off for the memory-care
    // brands). These three carried only RequireAuth while their sibling patient routes were module
    // gated, so a CerebraLinked patient could reach a subscription surface for a product that has
    // none — the patient dashboard already reasons this way ("CL mode: no subscriptions").
    path: "/subscription",
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="billing">
          <RequireAuth>
            <SubscriptionPage />
          </RequireAuth>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/subscription/success",
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="billing">
          <RequireAuth>
            <SubscriptionSuccessPage />
          </RequireAuth>
        </RequireModule>
      </RequireAudience>
    ),
  },
  {
    path: "/subscription/cancel",
    element: (
      <RequireAudience audience="patient">
        <RequireModule id="billing">
          <RequireAuth>
            <SubscriptionCancelPage />
          </RequireAuth>
        </RequireModule>
      </RequireAudience>
    ),
  },
  ...moduleRoutes("patient", "patient"),

  { path: "*", element: <PageNotFound /> },
  ]);
}

/**
 * The whole app hangs off one root route so RootLayout can pick the shell for whatever matched.
 * Exported as a factory so tests can mount the same tree against their own QueryClient.
 */
export function createRootRoute(client: QueryClient): RouteObject {
  return {
    path: "/",
    element: <RootLayout />,
    /**
     * Identity, before anything renders.
     *
     * It used to be fetched by `CerberusProvider` in an effect, which cannot run until after the
     * first paint — so a hard load spent one round trip rendering nothing (`RootLayout` returns
     * null while `loading`), and only then started the page's own fetches. Asking here moves that
     * request to the front of the load and lets the matched route's loader, which runs in the same
     * navigation, join it instead of waiting behind it.
     *
     * It resolves to null rather than failing: being signed out is the normal case, and a failure
     * is handled by the provider (which signs the user out) or by the shared client's 401
     * interceptor. Neither should take the shell down with an error boundary.
     */
    loader: async () => {
      await ensureIdentity(client);
      return null;
    },
    // Last resort. A failure in the shell itself has nowhere to render but in place of the shell;
    // per-route boundaries mean page failures never get this far.
    errorElement: <RouteError />,
    // Rendered while the initial navigation's loaders resolve. Blank on purpose: the shell already
    // renders nothing until identity arrives, so this matches what a cold load looks like today
    // rather than flashing a skeleton the shell would immediately replace.
    HydrateFallback: () => null,
    children: createAppRoutes(client),
  };
}

export const rootRoute = createRootRoute(appQueryClient);

export const router = createBrowserRouter([rootRoute]);
